import React, { Component } from 'react';
import { connect } from 'react-redux';
import { IonButton, IonIcon, IonInput, IonItem } from '@ionic/react';
import { add, alertCircleOutline, checkmark, checkmarkCircle, chevronBackOutline, chevronForwardOutline, trashOutline } from 'ionicons/icons';
import moment from 'moment';
import { withTranslation } from '../../lib/translate.js';
import { getConfig } from '../../appConfig.js';
import api from '../../lib/api.js';
import Basket from '../../lib/basket.js';
import { getGiftCardNumber, getMaskedGiftCardNumber, validateProfileData } from '../../lib/utils.js';
import { NormalText, SmallText, Spacer, StrongText, Subtitle } from '../common/index.js';
import Modal from '../modal/index.js';
import Incrementer from '../incrementer/index.js';
import { getGiftCards, setModal } from '../../store/actions.js';
import { ReactComponent as DiscountIcon } from '../../assets/images/discount.svg';
import { ReactComponent as EmailedGiftCardIcon } from '../../assets/images/gift-card-emailed.svg';
import { ReactComponent as PhysicalGiftCardIcon } from '../../assets/images/gift-card-physical.svg';
import './index.css';

const initialRedeemState = {
	physicalCode: '',
	physicalError: null,
	physicalChecking: false,
	physicalCard: null,
	voucherId: '',
	redemptionCode: '',
	emailedError: null,
	emailedLoading: false,
	emailedCard: null,
};

class GiftCardsCheckout extends Component {
	constructor(props) {
		super(props);
		this.state = {
			redeemModalOpen: false,
			redeemType: null,
			selectedGiftCardIds: [],
			...initialRedeemState,
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.giftCards !== this.props.giftCards && this.state.redeemType === 'list') {
			const prevIds = prevProps.giftCards.map((card) => card.id);
			const newIds = this.getAccountGiftCards().filter((card) => !prevIds.includes(card.id)).map((card) => card.id);
			if (newIds.length > 0) {
				this.setState((prev) => ({ selectedGiftCardIds: [...prev.selectedGiftCardIds, ...newIds] }));
			}
		}
	}

	isPhysicalOnly = () => getConfig().flags.removeGiftCardsFromBasket || !this.props.auth.loggedIn;

	getAccountGiftCards = () => {
		if (this.isPhysicalOnly()) return [];
		const today = moment().startOf('day');
		return this.props.giftCards
			.filter((card) => card.value_remaining > 0 && !moment(card.expiry_date, 'DD/MM/YYYY').isBefore(today))
			// Soonest-expiring first; a card with no expiry has no date and goes last.
			.sort((a, b) => (moment(a.expiry_date, 'DD/MM/YYYY').valueOf() || Infinity) - (moment(b.expiry_date, 'DD/MM/YYYY').valueOf() || Infinity));
	};

	getAmountToCover = (accountGiftCards) => {
		const accountIds = accountGiftCards.map((card) => card.id);
		const appliedAccountValue = Basket.getAppliedGiftCards()
			.filter((el) => accountIds.includes(el.id))
			.reduce((sum, el) => sum + (el.value || 0), 0);
		return Math.round((Basket.getTotal() + appliedAccountValue) * 100) / 100;
	};

	allocateGiftCards = (accountGiftCards, selectedIds) => {
		let left = this.getAmountToCover(accountGiftCards);
		const allocation = {};
		accountGiftCards
			.filter((card) => selectedIds.includes(card.id))
			.forEach((card) => {
				const value = Math.round(Math.min(card.value_remaining / 100, Math.max(left, 0)) * 100) / 100;
				allocation[card.id] = value;
				left = Math.round((left - value) * 100) / 100;
			});
		return allocation;
	};

	getInitialSelection = (accountGiftCards) => {
		const accountIds = accountGiftCards.map((card) => card.id);
		const appliedIds = Basket.getAppliedGiftCards()
			.filter((el) => el.value > 0 && accountIds.includes(el.id))
			.map((el) => el.id);
		if (appliedIds.length > 0) return appliedIds;
		let left = this.getAmountToCover(accountGiftCards);
		const selectedIds = [];
		for (const card of accountGiftCards) {
			if (left <= 0) break;
			selectedIds.push(card.id);
			left -= card.value_remaining / 100;
		}
		return selectedIds;
	};

	getAddCardType = () => {
		if (this.isPhysicalOnly()) return 'physical';
		return getConfig().flags.hasApplyGiftCardCodeOnCheckout ? null : 'emailed';
	};

	setRedeemModalOpen = (value) => {
		const accountGiftCards = this.getAccountGiftCards();
		this.setState({
			redeemModalOpen: value,
			redeemType: accountGiftCards.length > 0 ? 'list' : this.getAddCardType(),
			selectedGiftCardIds: value ? this.getInitialSelection(accountGiftCards) : [],
			...initialRedeemState,
		});
	};

	openGiftCards = () => {
		const { auth, profile, dispatch } = this.props;
		if (auth.loggedIn && !validateProfileData(profile).isValid) {
			dispatch(setModal('isVerfiedModalOpen', true));
		} else {
			this.setRedeemModalOpen(true);
		}
	};

	setRedeemType = (redeemType) => {
		this.setState({ redeemType, ...initialRedeemState });
	};

	toggleGiftCard = (id) => {
		const { selectedGiftCardIds } = this.state;
		this.setState({ selectedGiftCardIds: selectedGiftCardIds.includes(id) ? selectedGiftCardIds.filter((el) => el !== id) : [...selectedGiftCardIds, id] });
	};

	applyAccountGiftCards = (accountGiftCards, allocation) => {
		const accountIds = accountGiftCards.map((card) => card.id);
		Basket.setAppliedGiftCards([
			...Basket.getAppliedGiftCards().filter((el) => !accountIds.includes(el.id)),
			...accountGiftCards
				.filter((card) => allocation[card.id] > 0)
				.map((card) => ({ id: card.id, value: allocation[card.id], isTopUpCard: Number(card.is_topup_card), isEventCard: Number(card.is_event_card), ...(card.qr_code && { qr_code: card.qr_code }) })),
		]);
		this.setRedeemModalOpen(false);
	};

	getGiftCardExpiry = (card) => {
		const { __ } = this.props;
		const today = moment().startOf('day');
		const expiryDate = moment(card.expiry_date, 'DD/MM/YYYY').startOf('day');
		if (!expiryDate.isValid()) return { text: '', isWarning: false };
		const daysLeft = expiryDate.diff(today, 'days');
		if (daysLeft === 0) return { text: __('Expires today'), isWarning: true };
		if (daysLeft <= 30) return { text: `${__('Expires in')} ${daysLeft} ${__(daysLeft === 1 ? 'day' : 'days')}`, isWarning: true };
		return { text: `${__('Expires')} ${expiryDate.format('LLLL')}`, isWarning: false };
	};

	getGiftCardLabel = (card) => (card.is_topup_card ? 'Top Up card' : card.is_event_card ? 'Event card' : 'Gift card');

	renderGiftCardsList = () => {
		const { __ } = this.props;
		const { selectedGiftCardIds } = this.state;
		const accountGiftCards = this.getAccountGiftCards();
		const allocation = this.allocateGiftCards(accountGiftCards, selectedGiftCardIds);
		const { showFullGiftCardNumber } = getConfig().flags;
		const coveredAmount = Math.round(Object.values(allocation).reduce((sum, value) => sum + value, 0) * 100) / 100;
		const leftToPay = Math.round((this.getAmountToCover(accountGiftCards) - coveredAmount) * 100) / 100;
		return (
			<>
				<StrongText className="block text-to-update">{__('Your gift cards')}</StrongText>
				<SmallText className="block text-to-update">{__('Choose from your available gift card balances')}</SmallText>
				<Spacer size={2} />
				<div className="gift-cards-checkout-list">
					{accountGiftCards.map((card) => {
						const isSelected = selectedGiftCardIds.includes(card.id);
						const expiry = this.getGiftCardExpiry(card);
						return (
							<div
								key={card.id}
								className={`box-content flex-wrapper flex-align-center pointer gift-cards-checkout-list-item${isSelected ? ' gift-cards-checkout-list-item-selected' : ''}`}
								onClick={() => this.toggleGiftCard(card.id)}
							>
								<div className="flex-wrapper flex-align-center flex-justify-center gift-cards-checkout-list-checkbox">
									<IonIcon icon={checkmark} />
								</div>
								<div className="gift-cards-checkout-list-item-details">
									<StrongText className="block text-to-update">{showFullGiftCardNumber ? __(this.getGiftCardLabel(card)) : `${__(this.getGiftCardLabel(card))} ${getMaskedGiftCardNumber(card)}`}</StrongText>
									{showFullGiftCardNumber && <SmallText className="block">{getGiftCardNumber(card)}</SmallText>}
									<SmallText className={`block${expiry.isWarning ? ' danger-color bold' : ''}`}>{expiry.text}</SmallText>
								</div>
								<div className="gift-cards-checkout-list-item-amount">
									<StrongText className="block">{Basket.formatPrice(card.value_remaining / 100, true)}</StrongText>
									{isSelected && (
										<SmallText className="block primary-color bold">{allocation[card.id] > 0 ? `${__('Use')} ${Basket.formatPrice(allocation[card.id], true)}` : __('Not needed')}</SmallText>
									)}
								</div>
							</div>
						);
					})}
				</div>
				{/* <Spacer size={1} />
				<div className="box-content gift-cards-checkout-redeem-card-details flex-wrapper flex-align-center flex-justify-between">
					<NormalText className='text-to-update'>{coveredAmount > 0 ? `${__('Gift cards cover')} ${Basket.formatPrice(coveredAmount, true)}` : __('No cards selected')}</NormalText>
					<StrongText className='text-to-update'>{leftToPay > 0 ? `${Basket.formatPrice(leftToPay, true)} ${__('to pay')}` : __('Order covered')}</StrongText>
				</div> */}
				<Spacer size={2} />
				<IonButton expand="block" color="primary" className="button-uppercase" disabled={coveredAmount <= 0} onClick={() => this.applyAccountGiftCards(accountGiftCards, allocation)}>
					{coveredAmount > 0 ? `${__('Apply')} ${Basket.formatPrice(coveredAmount, true)}` : __('Apply')}
				</IonButton>
				<Spacer size={2} />
				<IonButton className='button-uppercase' expand="block" color="primary" fill="clear" onClick={() => this.setRedeemType(this.getAddCardType())}>
					<IonIcon slot="start" icon={add} />
					{__('Add another gift card')}
				</IonButton>
			</>
		);
	};

	checkPhysicalCard = async () => {
		const { __ } = this.props;
		const { physicalCode, physicalChecking } = this.state;
		if (physicalCode.length < 6 || physicalChecking) return;
		if (!this.isPhysicalOnly() && /^\d{8,}$/.test(physicalCode)) {
			this.setState({ physicalError: __('That looks like an emailed gift card number. Go back and choose Emailed gift voucher.') });
			return;
		}
		this.setState({ physicalChecking: true });
		try {
			const result = await api.sendGiftVoucherCode({ code: physicalCode });
			const card = result.data;
			if (result.error || result.Error || !card) {
				this.setState({ physicalChecking: false, physicalError: __("We couldn't find a card with that code. Check it and try again.") });
			} else if (Basket.checkout_gift_cards.some((el) => el.id === card.id)) {
				this.setState({ physicalChecking: false, physicalError: __("That card's already applied to this order.") });
			} else {
				this.setState({ physicalChecking: false, physicalCard: card });
			}
		} catch (err) {
			this.setState({ physicalChecking: false, physicalError: __("We couldn't find a card with that code. Check it and try again.") });
		}
	};

	applyPhysicalCard = (amount) => {
		const { physicalCard } = this.state;
		Basket.setCheckoutGiftCards([...Basket.checkout_gift_cards, physicalCard]);
		Basket.setAppliedGiftCards([
			...Basket.getAppliedGiftCards(),
			{ id: physicalCard.id, value: amount, isTopUpCard: Number(physicalCard.is_topup_card), isEventCard: Number(physicalCard.is_event_card), ...(physicalCard.qr_code && { qr_code: physicalCard.qr_code }) },
		]);
		this.setRedeemModalOpen(false);
	};

	checkEmailedVoucher = async () => {
		const { __ } = this.props;
		const { voucherId, redemptionCode } = this.state;
		this.setState({ emailedLoading: true });
		try {
			const result = await api.checkGiftVoucherBalance({ ...(voucherId && { voucher_id: voucherId }), ...(redemptionCode && { voucher_code: redemptionCode }) });
			if (result.error || !result.data) {
				this.setState({ emailedLoading: false, emailedError: __(result.error?.message || "We couldn't find a gift card with those details. Check both codes and try again.") });
			} else {
				this.setState({ emailedLoading: false, emailedCard: result.data });
			}
		} catch (err) {
			this.setState({ emailedLoading: false, emailedError: __(err.response?.data?.error?.message || "We couldn't find a gift card with those details. Check both codes and try again.") });
		}
	};

	redeemEmailedVoucher = async () => {
		const { __, dispatch } = this.props;
		const { voucherId, redemptionCode } = this.state;
		this.setState({ emailedLoading: true });
		try {
			const result = await api.redeemGiftVoucher({ ...(voucherId && { voucher_id: voucherId }), ...(redemptionCode && { code: redemptionCode }) });
			if (result.error) {
				this.setState({ emailedLoading: false, emailedCard: null, emailedError: __(result.error.message || 'An error occurred. Please try again') });
				return;
			}
			dispatch(getGiftCards());
			this.setRedeemType('list');
		} catch (err) {
			this.setState({ emailedLoading: false, emailedCard: null, emailedError: __(err.response?.data?.error?.message || 'An error occurred. Please try again') });
		}
	};

	renderRedeemHeader = (title) => {
		const { __ } = this.props;
		const hasAccountGiftCards = this.getAccountGiftCards().length > 0;
		const backType = getConfig().flags.hasApplyGiftCardCodeOnCheckout ? null : 'list';
		return (
			<>
				<div className="flex-wrapper flex-align-center gift-cards-checkout-redeem-header">
					{!this.isPhysicalOnly() && (backType === null || hasAccountGiftCards) && <IonIcon className="pointer" icon={chevronBackOutline} onClick={() => this.setRedeemType(backType)} />}
					<StrongText className='text-to-update'>{__(title)}</StrongText>
				</div>
				<Spacer size={2} />
			</>
		);
	};

	renderRedeemError = (error) => (
		<div className="flex-wrapper gift-cards-checkout-redeem-error">
			<IonIcon color="danger" icon={alertCircleOutline} />
			<SmallText className="danger-color">{error}</SmallText>
		</div>
	);

	renderEmailedVoucher = () => {
		const { __ } = this.props;
		const { voucherId, redemptionCode, emailedError, emailedLoading, emailedCard } = this.state;
		const { removeVoucherNumberFromGiftCardClaim, removeRedemptionCodeFromGiftCardClaim } = getConfig().flags;
		return (
			<>
				{this.renderRedeemHeader(!getConfig().flags.hasApplyGiftCardCodeOnCheckout ? 'Redeem gift voucher' : 'Emailed gift voucher')}
				{emailedCard ? (
					<>
						<div className="flex-wrapper flex-align-center gift-cards-checkout-redeem-header">
							<IonIcon color="success" icon={checkmarkCircle} />
							<StrongText className="success-color">{__('Card found')}</StrongText>
						</div>
						<Spacer size={1} />
						<div className="box-content gift-cards-checkout-redeem-card-details">
							<div className="flex-wrapper flex-align-center flex-justify-between">
								<NormalText>{__('Card')}</NormalText>
								<StrongText>{`•• ${String(voucherId || emailedCard.id || '').slice(-4)}`}</StrongText>
							</div>
							<Spacer size={0} />
							<div className="flex-wrapper flex-align-end flex-justify-between">
								<NormalText>{__('Balance')}</NormalText>
								<StrongText className="bold">{Basket.formatPrice(emailedCard.value_remaining / 100, true)}</StrongText>
							</div>
						</div>
						<Spacer size={1} />
						<SmallText className="block">{__('The card is saved to My gift cards.')}</SmallText>
						<Spacer size={2} />
						<IonButton expand="block" color="primary" className="button-uppercase" disabled={emailedLoading} onClick={this.redeemEmailedVoucher}>
							{__(emailedLoading ? 'Adding…' : 'Add to my account')}
						</IonButton>
					</>
				) : (
					<>
						<SmallText className="block">{__('Enter both codes from your gift card email. The card is saved to My gift cards.')}</SmallText>
						<Spacer size={2} />
						{!removeVoucherNumberFromGiftCardClaim && (
							<div className="input-field-container">
								<StrongText>{__('Voucher number')}</StrongText>
								<IonItem className="input-field-wrapper" lines="none">
									<IonInput
										placeholder={__('e.g. 40021880')}
										type="text"
										inputmode="numeric"
										pattern="[0-9]*"
										onIonInput={(e) => {
											const voucherId = (e.target.value || '').replace(/[^0-9]/g, '');
											e.target.value = voucherId;
											this.setState({ voucherId, emailedError: null });
										}}
										value={voucherId}
									></IonInput>
								</IonItem>
							</div>
						)}
						{!removeRedemptionCodeFromGiftCardClaim && (
							<div className="input-field-container">
								<StrongText>{__('Redemption code')}</StrongText>
								<IonItem className="input-field-wrapper" lines="none">
									<IonInput
										placeholder={__('e.g. FRYUP')}
										type="text"
										inputmode="text"
										autocapitalize="characters"
										maxlength={10}
										onIonInput={(e) => {
											const redemptionCode = (e.target.value || '').toUpperCase();
											e.target.value = redemptionCode;
											this.setState({ redemptionCode, emailedError: null });
										}}
										value={redemptionCode}
									></IonInput>
								</IonItem>
							</div>
						)}
						{emailedError && this.renderRedeemError(emailedError)}
						<Spacer size={2} />
						<IonButton expand="block" color="primary" className="button-uppercase" disabled={(!removeVoucherNumberFromGiftCardClaim && !voucherId) || (!removeRedemptionCodeFromGiftCardClaim && !redemptionCode) || emailedLoading} onClick={this.checkEmailedVoucher}>
							{__(emailedLoading ? 'Checking…' : 'Apply gift card')}
						</IonButton>
					</>
				)}
			</>
		);
	};

	renderPhysicalCard = () => {
		const { __ } = this.props;
		const { physicalCode, physicalError, physicalChecking, physicalCard } = this.state;
		const orderTotal = Basket.getTotal();
		const cardBalance = physicalCard ? physicalCard.value_remaining / 100 : 0;
		const amountToApply = Math.round(Math.min(cardBalance, orderTotal) * 100) / 100;
		return (
			<>
				{this.renderRedeemHeader('Physical gift card')}
				{physicalCard ? (
					<>

						<div className="flex-wrapper flex-align-center gift-cards-checkout-redeem-header">
							<IonIcon color="success" icon={checkmarkCircle} />
							<StrongText className="success-color">{__('Card found')}</StrongText>
						</div>
						<Spacer size={1} />
						<div className="box-content gift-cards-checkout-redeem-card-details">
							<div className="flex-wrapper flex-align-center flex-justify-between">
								<NormalText>{__('Card code')}</NormalText>
								<StrongText>{`•••• ${physicalCode.slice(-4)}`}</StrongText>
							</div>
							<Spacer size={0} />
							<div className="flex-wrapper flex-align-end flex-justify-between">
								<NormalText>{__('Balance')}</NormalText>
								<StrongText className="bold">{Basket.formatPrice(cardBalance, true)}</StrongText>
							</div>
						</div>
						<Spacer size={1} />
						<SmallText className="block">
							{__(cardBalance > orderTotal ? 'Only what this order needs is used. The remaining balance stays on the physical card.' : 'The full card balance is used on this order.')}
						</SmallText>
						<Spacer size={2} />
						<IonButton expand="block" color="primary" className="button-uppercase" disabled={orderTotal <= 0} onClick={() => this.applyPhysicalCard(amountToApply)}>
							{orderTotal > 0 ? `${__('Apply')} ${Basket.formatPrice(amountToApply, true)} ${__('to order')}` : __('Order already covered')}
						</IonButton>
					</>
				) : (
					<>
						<SmallText className="block">{__("Enter the code printed on the back of the card. It's used for this order only and isn't saved to your account.")}</SmallText>
						<Spacer size={2} />
						<div className="input-field-container">
							<StrongText>{__('Card code')}</StrongText>
							<IonItem className="input-field-wrapper" lines="none">
								<IonInput
									placeholder={__('e.g. EVT7Q2K9')}
									type="text"
									inputmode="text"
									autocapitalize="characters"
									maxlength={12}
									onIonInput={(e) => {
										const physicalCode = (e.target.value || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
										e.target.value = physicalCode;
										this.setState({ physicalCode, physicalError: null });
									}}
									value={physicalCode}
								></IonInput>
							</IonItem>
						</div>
						{physicalError && this.renderRedeemError(physicalError)}
						<Spacer size={2} />
						<IonButton expand="block" color="primary" className="button-uppercase" disabled={physicalCode.length < 6 || physicalChecking} onClick={this.checkPhysicalCard}>
							{__(physicalChecking ? 'Checking…' : 'Apply gift card')}
						</IonButton>
					</>
				)}
			</>
		);
	};

	setAppliedGiftCardValue = (id, value) => {
		Basket.setAppliedGiftCards(Basket.getAppliedGiftCards().map((el) => (el.id === id ? { ...el, value: Math.round(value * 100) / 100 } : el)));
	};

	removeAppliedGiftCard = (id) => {
		Basket.setCheckoutGiftCards(Basket.checkout_gift_cards.filter((card) => card.id !== id));
		Basket.setAppliedGiftCards(Basket.getAppliedGiftCards().filter((el) => el.id !== id));
	};

	renderAppliedGiftCards = (appliedGiftCards, canChange) => {
		const { __ } = this.props;
		const orderTotal = Basket.getTotal();
		const { showFullGiftCardNumber } = getConfig().flags;
		return (
			<div className="gift-cards-checkout-applied">
				<div className="flex-wrapper flex-align-center flex-justify-between">
					<StrongText className='text-to-update'>{__('Gift cards applied')}</StrongText>
					{canChange && (
						<StrongText className="primary-color pointer" onClick={this.openGiftCards}>
							{__('Change')}
						</StrongText>
					)}
				</div>
				<Spacer size={1} />
				{appliedGiftCards.map(({ card, value, isPhysical }) => {
					const balance = card.value_remaining / 100;
					const maxValue = Math.round(Math.min(balance, value + orderTotal) * 100) / 100;
					return (
						<div key={card.id} className="flex-wrapper flex-align-center gift-cards-checkout-applied-item">
							<div className="gift-cards-checkout-applied-item-details">
								<StrongText className="block text-to-update">{showFullGiftCardNumber ? __(isPhysical ? 'Physical card' : this.getGiftCardLabel(card)) : `${__(isPhysical ? 'Physical card' : this.getGiftCardLabel(card))} ${getMaskedGiftCardNumber(card)}`}</StrongText>
								{showFullGiftCardNumber && <SmallText className="block">{getGiftCardNumber(card)}</SmallText>}
								<SmallText className="block text-to-update">{__(isPhysical ? '{balance} left · not saved' : '{balance} left on card', { balance: Basket.formatPrice(balance - value, true) })}</SmallText>
							</div>
							<div className="gift-cards-checkout-stepper">
								<Incrementer
									allowInput
									allowDecimal
									allowNegative={false}
									quantity={value}
									minLimit={Math.min(1, maxValue)}
									maxLimit={maxValue}
									onUpdate={(newValue) => this.setAppliedGiftCardValue(card.id, newValue)}
								/>
							</div>
							<IonIcon className="pointer gift-cards-checkout-applied-remove" icon={trashOutline} onClick={() => this.removeAppliedGiftCard(card.id)} />
						</div>
					);
				})}
			</div>
		);
	};

	render() {
		const { __, giftCards } = this.props;
		const { redeemModalOpen, redeemType } = this.state;
		const isPhysicalOnly = this.isPhysicalOnly();
		if (isPhysicalOnly && !getConfig().flags.hasApplyGiftCardCodeOnCheckout) return null;
		const accountGiftCards = this.getAccountGiftCards();
		const accountGiftCardsBalance = accountGiftCards.reduce((sum, card) => sum + card.value_remaining, 0);
		const appliedGiftCards = Basket.getAppliedGiftCards()
			.filter((el) => el.value > 0)
			.map((el) => {
				const physicalCard = Basket.checkout_gift_cards.find((card) => card.id === el.id);
				return { card: physicalCard || giftCards.find((card) => card.id === el.id), value: el.value, isPhysical: !!physicalCard };
			})
			.filter((el) => el.card && (!isPhysicalOnly || el.isPhysical));
		const hasAppliedAccountGiftCards = appliedGiftCards.some((el) => !el.isPhysical);
		const hasAppliedPoints = Basket.getAppliedPoints() > 0;
		const isProfileValid = !this.props.auth.loggedIn || validateProfileData(this.props.profile).isValid;
		const redeemOptions = [
			{ type: 'emailed', Icon: EmailedGiftCardIcon, title: 'Emailed gift voucher', description: 'Voucher number + redemption code', note: 'Saved to your account for next time' },
			{ type: 'physical', Icon: PhysicalGiftCardIcon, title: 'Physical gift card', description: 'Code on the back of the card', note: 'Used for this order only' },
		];
		return (
			<div className="order-summary-additional-buttons-wrapper gift-cards-checkout">
				<Spacer size={5} />
				<StrongText className="text-to-update">{__('Gift cards')}</StrongText>
				<Spacer size={1} />
				{accountGiftCards.length > 0 && !hasAppliedAccountGiftCards && (
					<IonButton expand="block" color="primary" fill="clear" className={`${isProfileValid ? '' : 'disabled '}order-summary-additional-button gift-cards-checkout-use-button`} disabled={hasAppliedPoints} onClick={this.openGiftCards}>
						{getConfig().general?.basketConfig?.redeemVouchersIcon ? <img src={getConfig().general.basketConfig.redeemVouchersIcon} /> : <DiscountIcon />}
						<div className="gift-cards-checkout-use-text">
							<NormalText className="block text-to-update">{__('Use gift card balance')}</NormalText>
							<SmallText className="block text-to-update">{`${Basket.formatPrice(accountGiftCardsBalance / 100, true)} ${__('available')}`}</SmallText>
						</div>
						<IonIcon style={{ height: '18px', width: '18px' }} icon={chevronForwardOutline}></IonIcon>
					</IonButton>
				)}
				{accountGiftCards.length === 0 && !hasAppliedAccountGiftCards && (
					<>
						<IonButton expand="block" color="primary" fill="clear" className={`${isProfileValid ? '' : 'disabled '}order-summary-additional-button`} disabled={hasAppliedPoints} onClick={this.openGiftCards}>
							{getConfig().general?.basketConfig?.redeemVouchersIcon ? <img src={getConfig().general.basketConfig.redeemVouchersIcon} /> : <DiscountIcon />}
							<NormalText className="text-to-update">{__('Redeem gift cards')}</NormalText>
							<IonIcon style={{ height: '18px', width: '18px' }} icon={chevronForwardOutline}></IonIcon>
						</IonButton>
						{!isPhysicalOnly && giftCards.length > 0 && <SmallText className="block">{__('No balance left on your linked gift cards')}</SmallText>}
					</>
				)}
				{appliedGiftCards.length > 0 && this.renderAppliedGiftCards(appliedGiftCards, accountGiftCards.length > 0)}
				<Modal onDidDismiss={() => this.setRedeemModalOpen(false)} isOpen={redeemModalOpen} className="modal-type-alert gift-cards-checkout-modal">
					{redeemType === 'list' ? (
						this.renderGiftCardsList()
					) : redeemType === 'physical' ? (
						this.renderPhysicalCard()
					) : redeemType === 'emailed' ? (
						this.renderEmailedVoucher()
					) : (
						<>
							<div className="flex-wrapper flex-align-center gift-cards-checkout-redeem-header">
								{this.getAccountGiftCards().length > 0 && <IonIcon className="pointer" icon={chevronBackOutline} onClick={() => this.setRedeemType('list')} />}
								<StrongText>{__('Which gift card do you have?')}</StrongText>
							</div>
							<SmallText className="block text-to-update">{__("We'll ask for the right code next")}</SmallText>
							<Spacer size={2} />
							<div className="gift-cards-checkout-redeem-options">
								{redeemOptions.map((option) => (
									<div key={option.type} className="gift-cards-checkout-redeem-option pointer box-content" onClick={() => this.setRedeemType(option.type)}>
										<div className="gift-cards-checkout-redeem-option-icon flex-wrapper flex-align-center flex-justify-center">
											<option.Icon />
										</div>
										<div>
											<SmallText className="block bold text-to-update">{__(option.title)}</SmallText>
											<SmallText className="block">{__(option.description)}</SmallText>
										</div>
										<SmallText className="block gift-cards-checkout-redeem-option-note">{__(option.note)}</SmallText>
									</div>
								))}
							</div>
						</>
					)}
				</Modal>
			</div>
		);
	}
}

const mapStateToProps = (store) => {
	return {
		giftCards: store.profile.giftCards || [],
		basketUpdated: store.orders.basketUpdated,
		profile: store.profile.profile,
		auth: store.profile.auth,
	};
};

export default connect(mapStateToProps)(withTranslation(GiftCardsCheckout));
